import { combineReducers } from 'redux';
import { call, debounce, put, takeLatest } from 'redux-saga/effects';

import { api } from './api';
import { ExtendedAxiosResponse } from 'helpers/apiClient';
import {
  AppAction,
  createActionCreator,
  createActionType,
  createApiActionCreators,
  createLoadingStateReducer,
  createReducer,
  LoadingStatus,
  RequestActionTypes,
} from 'helpers/redux/reduxHelpers';
import { AppState } from 'helpers/store/models/AppState';
import { Address } from 'models/Address';
import { ApiClientRequest } from 'models/ApiClientRequest';
import { performApiCall } from 'saga/apiClient/ducks';
import { selectProperty } from 'saga/property/ducks';

/* CONSTANTS */
const debounceTime = 2000;

/* STATE */
export interface AddressSearchState {
  addresses: Address[];
  loadingStatus: LoadingStatus;
}

/* ACTION TYPES */
export enum AddressSearchActionTypes {
  GetAddresses = '@@AddressSearch/GET_ADDRESSES',
  GetAddressesDebounced = '@@AddressSearch/GET_ADDRESSES_DEBOUNCED',
  ClearAddresses = '@@AddressSearch/CLEAR_ADDRESSES',
  SetAddressLoading = '@@AddressSearch/SET_ADDRESS_LOADING',
}

/* ACTIONS */
export const getAddressesActions = createApiActionCreators(AddressSearchActionTypes.GetAddresses);
export const getAddressesDebouncedActions = createActionCreator(AddressSearchActionTypes.GetAddressesDebounced);
export const clearAddressesAction = createActionCreator(AddressSearchActionTypes.ClearAddresses);
export const setAddressLoading = createActionCreator(AddressSearchActionTypes.SetAddressLoading);

/* REDUCERS */
const initialState: AddressSearchState = {
  addresses: [],
  loadingStatus: LoadingStatus.success,
};

const addresses = createReducer(initialState.addresses, {
  [AddressSearchActionTypes.GetAddresses]: {
    [RequestActionTypes.SUCCESS]: (state: Address[], payload: Address[]) => payload,
    [RequestActionTypes.FAILURE]: () => initialState.addresses,
  },
  [AddressSearchActionTypes.ClearAddresses]: () => initialState.addresses,
});

const loadingStatus = createLoadingStateReducer(
  initialState.loadingStatus,
  {
    [AddressSearchActionTypes.GetAddresses]: [
      RequestActionTypes.REQUEST,
      RequestActionTypes.SUCCESS,
      RequestActionTypes.FAILURE,
    ],
  },
  {
    [AddressSearchActionTypes.SetAddressLoading]: (state: LoadingStatus, status: LoadingStatus) => status,
  }
);

export default combineReducers<AddressSearchState>({
  addresses,
  loadingStatus,
});

/* SELECTORS*/
const selectAddressSearch = (state: AppState): AddressSearchState => state.addressSearch;

export const selectAddressSearchAddresses = (state: AppState): Address[] => selectAddressSearch(state).addresses;

export const selectAddressSearchLoadingStatus = (state: AppState): LoadingStatus =>
  selectAddressSearch(state).loadingStatus;

/* SAGAS */
function* getAddresses({ payload: value }: AppAction<string>) {
  yield put(setAddressLoading(LoadingStatus.loading));

  const url: string = selectProperty('gui.AddressSearch.url');

  if (url) {
    const request: ApiClientRequest = {
      apiCall: call(api.getAddresses, url, value),
    };
    const resp: ExtendedAxiosResponse = yield call(performApiCall, request);

    if (resp.ok) {
      yield put(getAddressesActions.success(resp.data?.addresses?.addresses || []));
    } else {
      yield put(getAddressesActions.failure());
    }
  } else {
    yield put(getAddressesActions.failure());
  }
}

/* EXPORT */
export function* addressSearchSaga() {
  yield takeLatest(createActionType(AddressSearchActionTypes.GetAddresses, RequestActionTypes.REQUEST), getAddresses);
  yield debounce(debounceTime, AddressSearchActionTypes.GetAddressesDebounced, getAddresses);
}
